import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { 
  Activity, 
  Users, 
  TrendingUp, 
  Loader,
  RefreshCw,
  Monitor,
  LogIn,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'

interface UserActivity {
  user_id: string
  email: string
  full_name: string | null
  company: string | null
  total_logins: number
  total_app_accesses: number
  last_login: string | null
  last_activity: string | null
  unique_apps_used: number
}

interface AppUsageStats {
  application_id: string
  app_name: string
  app_type: string
  total_accesses: number
  unique_users: number
  last_accessed: string | null
  avg_session_duration: number | null
}

interface ActivityTimeline {
  activity_time: string
  activity_type: 'login' | 'app_access'
  user_email: string
  user_name: string | null
  app_name: string | null
  details: string
}

export const UserActivityDashboard: React.FC = () => {
  const ADMIN_EMAIL = 'sid.kaul@solharbor.com'
  const [userActivities, setUserActivities] = useState<UserActivity[]>([])
  const [appStats, setAppStats] = useState<AppUsageStats[]>([])
  const [timeline, setTimeline] = useState<ActivityTimeline[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [timeRange, setTimeRange] = useState('7 days')
  const [activeTab, setActiveTab] = useState<'timeline' | 'overview' | 'users' | 'apps'>('timeline')
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const ITEMS_PER_PAGE = 20

  useEffect(() => {
    fetchActivityData()
    setCurrentPage(1) // Reset to first page when time range changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange])

  useEffect(() => {
    // Reset to first page when switching tabs
    if (activeTab === 'timeline') {
      setCurrentPage(1)
    }
  }, [activeTab])

  const fetchActivityData = async () => {
    if (!loading) setRefreshing(true)
    setError(null)
    
    try {
      // Fetch user activity summary
      const { data: userActivityData, error: userError } = await supabase
        .rpc('get_user_activity_summary', { time_range: timeRange })
      
      if (userError) {
        console.error('Error fetching user activity:', userError)
        // Don't throw, just log and continue with empty data
        setUserActivities([])
      } else {
        // Filter out admin user
        const filteredUsers = (userActivityData || []).filter(
          (user: UserActivity) => user.email !== ADMIN_EMAIL
        )
        setUserActivities(filteredUsers)
      }

      // Fetch app usage stats
      const { data: appStatsData, error: appError } = await supabase
        .rpc('get_app_usage_stats', { time_range: timeRange })
      
      if (appError) {
        console.error('Error fetching app stats:', appError)
        setAppStats([])
      } else {
        setAppStats(appStatsData || [])
      }

      // Fetch activity timeline - use full time range for timeline tab
      const { data: timelineData, error: timelineError } = await supabase
        .rpc('get_activity_timeline', { 
          time_range: timeRange 
        })
      
      if (timelineError) {
        console.error('Error fetching timeline:', timelineError)
        setTimeline([])
      } else {
        // Filter out admin activities
        const filteredTimeline = (timelineData || []).filter(
          (activity: ActivityTimeline) => activity.user_email !== ADMIN_EMAIL
        )
        setTimeline(filteredTimeline)
      }

      // If all three failed, show an error
      if (userError && appError && timelineError) {
        setError('Unable to load activity data. The activity tracking feature may not be set up yet.')
      }

    } catch (error) {
      console.error('Error fetching activity data:', error)
      setError('An unexpected error occurred while loading activity data.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    
    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      return `${diffMinutes} min ago`
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
    } else {
      const diffDays = Math.floor(diffHours / 24)
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
    }
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  // Calculate overall stats
  const totalActiveUsers = userActivities.filter(u => u.last_activity).length
  const totalLogins = userActivities.reduce((sum, u) => sum + u.total_logins, 0)
  const totalAppAccesses = userActivities.reduce((sum, u) => sum + u.total_app_accesses, 0)
  const mostActiveApp = appStats.length > 0 ? appStats[0] : null

  if (loading) {
    return (
      <div style={{ 
        padding: '48px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Loader style={{ 
          height: '32px', 
          width: '32px', 
          color: '#2563eb',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    )
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Error Message */}
      {error && (
        <div style={{
          padding: '16px',
          marginBottom: '24px',
          borderRadius: '8px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          color: '#991b1b',
          fontFamily: 'Lexend Deca, sans-serif'
        }}>
          {error}
        </div>
      )}

      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '32px'
      }}>
        <div>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            color: '#111827',
            fontFamily: 'Lexend Deca, sans-serif',
            marginBottom: '8px'
          }}>
            User Activity Dashboard
          </h2>
          <p style={{ 
            fontSize: '14px', 
            color: '#6b7280',
            fontFamily: 'Lexend Deca, sans-serif'
          }}>
            Monitor user engagement and application usage
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '14px',
              fontFamily: 'Lexend Deca, sans-serif',
              background: 'white'
            }}
          >
            <option value="1 hour">Last hour</option>
            <option value="24 hours">Last 24 hours</option>
            <option value="7 days">Last 7 days</option>
            <option value="30 days">Last 30 days</option>
            <option value="90 days">Last 90 days</option>
          </select>
          
          <button
            onClick={fetchActivityData}
            disabled={refreshing}
            style={{
              padding: '8px 12px',
              background: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              fontFamily: 'Lexend Deca, sans-serif'
            }}
          >
            <RefreshCw style={{ 
              height: '14px', 
              width: '14px',
              animation: refreshing ? 'spin 1s linear infinite' : 'none'
            }} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <p style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px', fontFamily: 'Lexend Deca, sans-serif' }}>
                Active Users
              </p>
              <p style={{ fontSize: '32px', fontWeight: 'bold', fontFamily: 'Lexend Deca, sans-serif' }}>
                {totalActiveUsers}
              </p>
            </div>
            <Users style={{ height: '24px', width: '24px', opacity: 0.8 }} />
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <p style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px', fontFamily: 'Lexend Deca, sans-serif' }}>
                Total Logins
              </p>
              <p style={{ fontSize: '32px', fontWeight: 'bold', fontFamily: 'Lexend Deca, sans-serif' }}>
                {totalLogins}
              </p>
            </div>
            <LogIn style={{ height: '24px', width: '24px', opacity: 0.8 }} />
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <p style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px', fontFamily: 'Lexend Deca, sans-serif' }}>
                App Accesses
              </p>
              <p style={{ fontSize: '32px', fontWeight: 'bold', fontFamily: 'Lexend Deca, sans-serif' }}>
                {totalAppAccesses}
              </p>
            </div>
            <Monitor style={{ height: '24px', width: '24px', opacity: 0.8 }} />
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <p style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px', fontFamily: 'Lexend Deca, sans-serif' }}>
                Most Used App
              </p>
              <p style={{ fontSize: '18px', fontWeight: 'bold', fontFamily: 'Lexend Deca, sans-serif' }}>
                {mostActiveApp?.app_name || 'N/A'}
              </p>
            </div>
            <TrendingUp style={{ height: '24px', width: '24px', opacity: 0.8 }} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '0'
      }}>
        {(['timeline', 'overview', 'users', 'apps'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
              color: activeTab === tab ? '#2563eb' : '#6b7280',
              fontWeight: activeTab === tab ? '600' : '400',
              cursor: 'pointer',
              fontSize: '14px',
              fontFamily: 'Lexend Deca, sans-serif',
              marginBottom: '-2px',
              textTransform: 'capitalize'
            }}
          >
            {tab === 'timeline' ? 'Recent Activity' : tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gap: '24px' }}>
            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '16px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                Quick Stats
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Avg. Apps per User
                  </p>
                  <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', fontFamily: 'Lexend Deca, sans-serif' }}>
                    {totalActiveUsers > 0 ? (userActivities.reduce((sum, u) => sum + u.unique_apps_used, 0) / totalActiveUsers).toFixed(1) : '0'}
                  </p>
                </div>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Avg. Logins per User
                  </p>
                  <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', fontFamily: 'Lexend Deca, sans-serif' }}>
                    {totalActiveUsers > 0 ? (totalLogins / totalActiveUsers).toFixed(1) : '0'}
                  </p>
                </div>
                <div style={{ padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Total Applications
                  </p>
                  <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', fontFamily: 'Lexend Deca, sans-serif' }}>
                    {appStats.length}
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '16px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                Top Active Users
              </h3>
              <div style={{ display: 'grid', gap: '8px' }}>
                {userActivities.slice(0, 5).map(user => (
                  <div key={user.user_id} style={{
                    padding: '12px',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#111827', fontFamily: 'Lexend Deca, sans-serif' }}>
                        {user.full_name || user.email}
                      </p>
                      <p style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                        {user.company || 'No company'}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#2563eb', fontFamily: 'Lexend Deca, sans-serif' }}>
                        {user.total_app_accesses} accesses
                      </p>
                      <p style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                        {formatDate(user.last_activity)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    User
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Company
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Logins
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    App Accesses
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Apps Used
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Last Login
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Last Activity
                  </th>
                </tr>
              </thead>
              <tbody>
                {userActivities.map(user => (
                  <tr key={user.user_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px' }}>
                      <div>
                        <p style={{ fontSize: '14px', fontWeight: '500', color: '#111827', fontFamily: 'Lexend Deca, sans-serif' }}>
                          {user.full_name || 'No name'}
                        </p>
                        <p style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                          {user.email}
                        </p>
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                      {user.company || '-'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '500', color: '#111827', fontFamily: 'Lexend Deca, sans-serif' }}>
                      {user.total_logins}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '500', color: '#111827', fontFamily: 'Lexend Deca, sans-serif' }}>
                      {user.total_app_accesses}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 8px',
                        background: '#e0e7ff',
                        color: '#3730a3',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        fontFamily: 'Lexend Deca, sans-serif'
                      }}>
                        {user.unique_apps_used}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                      {formatDate(user.last_login)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                      {formatDate(user.last_activity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Apps Tab */}
        {activeTab === 'apps' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Application
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Type
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Total Accesses
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Unique Users
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Avg. Session
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                    Last Accessed
                  </th>
                </tr>
              </thead>
              <tbody>
                {appStats.map(app => (
                  <tr key={app.application_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px' }}>
                      <p style={{ fontSize: '14px', fontWeight: '500', color: '#111827', fontFamily: 'Lexend Deca, sans-serif' }}>
                        {app.app_name}
                      </p>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        background: app.app_type === 'native' ? '#e0e7ff' : '#fef3c7',
                        color: app.app_type === 'native' ? '#3730a3' : '#92400e',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        fontFamily: 'Lexend Deca, sans-serif'
                      }}>
                        {app.app_type}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '500', color: '#111827', fontFamily: 'Lexend Deca, sans-serif' }}>
                      {app.total_accesses}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '500', color: '#111827', fontFamily: 'Lexend Deca, sans-serif' }}>
                      {app.unique_users}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                      {formatDuration(app.avg_session_duration)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                      {formatDate(app.last_accessed)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (() => {
          const totalPages = Math.ceil(timeline.length / ITEMS_PER_PAGE)
          const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
          const endIndex = startIndex + ITEMS_PER_PAGE
          const paginatedTimeline = timeline.slice(startIndex, endIndex)

          return (
            <div>
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ fontSize: '14px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                  Showing {startIndex + 1}-{Math.min(endIndex, timeline.length)} of {timeline.length} activities
                </p>
                {totalPages > 1 && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '6px 12px',
                        background: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: currentPage === 1 ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '14px',
                        fontFamily: 'Lexend Deca, sans-serif'
                      }}
                    >
                      <ChevronLeft style={{ height: '14px', width: '14px' }} />
                      Previous
                    </button>
                    <span style={{ 
                      padding: '6px 12px', 
                      fontSize: '14px', 
                      color: '#6b7280',
                      fontFamily: 'Lexend Deca, sans-serif'
                    }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: '6px 12px',
                        background: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        opacity: currentPage === totalPages ? 0.5 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '14px',
                        fontFamily: 'Lexend Deca, sans-serif'
                      }}
                    >
                      Next
                      <ChevronRight style={{ height: '14px', width: '14px' }} />
                    </button>
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gap: '8px' }}>
                {paginatedTimeline.map((activity, index) => (
                  <div key={startIndex + index} style={{
                  padding: '12px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: activity.activity_type === 'login' ? '#dcfce7' : '#e0e7ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {activity.activity_type === 'login' ? 
                      <LogIn style={{ height: '16px', width: '16px', color: '#166534' }} /> :
                      <Activity style={{ height: '16px', width: '16px', color: '#3730a3' }} />
                    }
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '14px', color: '#111827', fontFamily: 'Lexend Deca, sans-serif' }}>
                      <strong>{activity.user_name || activity.user_email}</strong>
                      {activity.activity_type === 'login' ? ' logged in' : ` accessed ${activity.app_name}`}
                    </p>
                    <p style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif' }}>
                      {activity.user_email} • {formatDate(activity.activity_time)}
                    </p>
                  </div>
                </div>
              ))}
                {timeline.length === 0 && (
                  <div style={{
                    padding: '32px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontFamily: 'Lexend Deca, sans-serif'
                  }}>
                    No activity recorded in the selected time range
                  </div>
                )}
              </div>
            </div>
          )
        })()}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}